import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { BlindsEnquiry, CustomerDetails, AppointmentDetails, QuoteDetails, Outcome, BlindAwningSpecification, Attachment, FormStepId } from '../types';
import { supabaseService } from '../services/supabaseService';

interface EnquiryStore {
  currentEnquiry: BlindsEnquiry;
  enquiries: any[]; // Changed to simple list for database
  currentStep: FormStepId;
  isFormValid: boolean;
  isLoading: boolean;
  isOnline: boolean; // Track online/offline status
  
  // Actions
  updateCustomerDetails: (details: Partial<CustomerDetails>) => void;
  updateAppointmentDetails: (details: Partial<AppointmentDetails>) => void;
  updateQuoteDetails: (details: Partial<QuoteDetails>) => void;
  updateOutcome: (outcome: Partial<Outcome>) => void;
  addSpecification: () => void;
  updateSpecification: (id: string, spec: Partial<BlindAwningSpecification>) => void;
  removeSpecification: (id: string) => void;
  addAttachment: (attachment: Attachment) => void;
  removeAttachment: (id: string) => void;
  setCurrentStep: (step: FormStepId) => void;
  saveEnquiry: () => Promise<void>;
  loadEnquiry: (id: string) => Promise<void>;
  loadAllEnquiries: () => Promise<void>;
  createNewEnquiry: () => void;
  deleteEnquiry: (id: string) => Promise<void>;
  validateForm: () => void;
  testConnection: () => Promise<void>;
  testStorageBucket: () => Promise<void>;
  toggleOnlineMode: () => void;
}

const createEmptyEnquiry = (): BlindsEnquiry => ({
  id: crypto.randomUUID(),
  customerDetails: {
    name: '',
    address: '',
    phone: '',
    mobile: '',
    email: '',
    howHeard: '',
    reasonForEnquiry: '',
    notes: ''
  },
  appointmentDetails: {
    date: '',
    time: '',
    samplesToTake: false,
    description: ''
  },
  quoteDetails: {
    dateSent: '',
    method: 'Email',
    followUpDate: '',
    comments: '',
    staffMember: ''
  },
  outcome: {
    status: 'Successful',
    notes: '',
    flagForAccounts: false
  },
  specifications: [],
  attachments: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

const createEmptySpecification = (): BlindAwningSpecification => ({
  id: crypto.randomUUID(),
  itemNumber: '',
  product: '',
  location: '',
  quantity: 1,
  fit: '',
  fixing: '',
  width: 0,
  drop: 0,
  material: '',
  bottomRail: '',
  control: '',
  stack: '',
  comments: ''
});

export const useEnquiryStore = create<EnquiryStore>()(
  devtools(
    persist(
      (set, get) => ({
        currentEnquiry: createEmptyEnquiry(),
        enquiries: [],
        currentStep: 'customer',
        isFormValid: false,
        isLoading: false,
        isOnline: true,

        updateCustomerDetails: (details) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              customerDetails: {
                ...state.currentEnquiry.customerDetails,
                ...details
              },
              updatedAt: new Date().toISOString()
            }
          }));
          get().validateForm();
        },

        updateAppointmentDetails: (details) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              appointmentDetails: {
                ...state.currentEnquiry.appointmentDetails,
                ...details
              },
              updatedAt: new Date().toISOString()
            }
          }));
          get().validateForm();
        },

        updateQuoteDetails: (details) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              quoteDetails: {
                ...state.currentEnquiry.quoteDetails,
                ...details
              },
              updatedAt: new Date().toISOString()
            }
          }));
          get().validateForm();
        },

        updateOutcome: (outcome) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              outcome: {
                ...state.currentEnquiry.outcome,
                ...outcome
              },
              updatedAt: new Date().toISOString()
            }
          }));
          get().validateForm();
        },

        addSpecification: () => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              specifications: [
                ...state.currentEnquiry.specifications,
                createEmptySpecification()
              ],
              updatedAt: new Date().toISOString()
            }
          }));
        },

        updateSpecification: (id, spec) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              specifications: state.currentEnquiry.specifications.map(s =>
                s.id === id ? { ...s, ...spec } : s
              ),
              updatedAt: new Date().toISOString()
            }
          }));
        },

        removeSpecification: (id) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              specifications: state.currentEnquiry.specifications.filter(s => s.id !== id),
              updatedAt: new Date().toISOString()
            }
          }));
        },

        addAttachment: (attachment) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              attachments: [...state.currentEnquiry.attachments, attachment],
              updatedAt: new Date().toISOString()
            }
          }));
        },

        removeAttachment: (id) => {
          set((state) => ({
            currentEnquiry: {
              ...state.currentEnquiry,
              attachments: state.currentEnquiry.attachments.filter(a => a.id !== id),
              updatedAt: new Date().toISOString()
            }
          }));
        },

        setCurrentStep: (step) => {
          set({ currentStep: step });
        },

        // NEW: Save to Supabase database
        saveEnquiry: async () => {
          const { currentEnquiry, isOnline } = get();
          
          if (!isOnline) {
            // Fallback to local storage if offline
            const { enquiries } = get();
            const existingIndex = enquiries.findIndex((e: any) => e.id === currentEnquiry.id);
            
            if (existingIndex >= 0) {
              set({
                enquiries: enquiries.map((e: any, index) =>
                  index === existingIndex ? currentEnquiry : e
                )
              });
            } else {
              set({
                enquiries: [...enquiries, currentEnquiry]
              });
            }
            alert('Saved locally (offline mode)');
            return;
          }

          set({ isLoading: true });
          
          try {
            const result = await supabaseService.saveEnquiry(currentEnquiry);
            
            if (result.success) {
              alert('Enquiry saved to database successfully!');
              // Refresh the enquiries list
              await get().loadAllEnquiries();
            } else {
              alert(`Error saving enquiry: ${result.error}`);
            }
          } catch (error) {
            console.error('Save error:', error);
            alert('Error saving enquiry. Please try again.');
          } finally {
            set({ isLoading: false });
          }
        },

        // NEW: Load from Supabase database
        loadEnquiry: async (id: string) => {
          const { isOnline } = get();
          
          if (!isOnline) {
            // Fallback to local storage if offline
            const { enquiries } = get();
            const enquiry = enquiries.find((e: any) => e.id === id);
            if (enquiry) {
              set({ currentEnquiry: enquiry });
            }
            return;
          }

          set({ isLoading: true });
          
          try {
            const result = await supabaseService.loadEnquiry(id);
            
            if (result.success && result.enquiry) {
              set({ currentEnquiry: result.enquiry });
            } else {
              alert(`Error loading enquiry: ${result.error}`);
            }
          } catch (error) {
            console.error('Load error:', error);
            alert('Error loading enquiry. Please try again.');
          } finally {
            set({ isLoading: false });
          }
        },

        // NEW: Load all enquiries from database
        loadAllEnquiries: async () => {
          const { isOnline } = get();
          
          if (!isOnline) {
            return; // Use local enquiries
          }

          set({ isLoading: true });
          
          try {
            const result = await supabaseService.loadAllEnquiries();
            
            if (result.success && result.enquiries) {
              set({ enquiries: result.enquiries });
            } else {
              console.error('Error loading enquiries:', result.error);
            }
          } catch (error) {
            console.error('Load enquiries error:', error);
          } finally {
            set({ isLoading: false });
          }
        },

        createNewEnquiry: () => {
          set({
            currentEnquiry: createEmptyEnquiry(),
            currentStep: 'customer'
          });
        },

        // NEW: Delete from database
        deleteEnquiry: async (id: string) => {
          const { isOnline } = get();
          
          if (!isOnline) {
            // Fallback to local storage if offline
            set((state) => ({
              enquiries: state.enquiries.filter((e: any) => e.id !== id)
            }));
            return;
          }

          set({ isLoading: true });
          
          try {
            const result = await supabaseService.deleteEnquiry(id);
            
            if (result.success) {
              // Refresh the enquiries list
              await get().loadAllEnquiries();
              alert('Enquiry deleted successfully!');
            } else {
              alert(`Error deleting enquiry: ${result.error}`);
            }
          } catch (error) {
            console.error('Delete error:', error);
            alert('Error deleting enquiry. Please try again.');
          } finally {
            set({ isLoading: false });
          }
        },

        validateForm: () => {
          const { currentEnquiry } = get();
          const { customerDetails, appointmentDetails } = currentEnquiry;
          
          const isValid = !!(
            customerDetails.name &&
            customerDetails.address &&
            customerDetails.phone &&
            appointmentDetails.date &&
            appointmentDetails.time
          );
          
          set({ isFormValid: isValid });
        },

        // NEW: Test database connection
        testConnection: async () => {
          set({ isLoading: true });
          
          try {
            const result = await supabaseService.testConnection();
            
            if (result.success) {
              alert('✅ Database connection successful!');
              set({ isOnline: true });
            } else {
              alert(`❌ Database connection failed: ${result.error}`);
              set({ isOnline: false });
            }
          } catch (error) {
            console.error('Connection test error:', error);
            alert('❌ Database connection failed');
            set({ isOnline: false });
          } finally {
            set({ isLoading: false });
          }
        },

        // NEW: Test storage bucket
        testStorageBucket: async () => {
          set({ isLoading: true });
          
          try {
            const result = await supabaseService.testStorageBucket();
            
            if (result.success) {
              const bucketInfo = result.bucketInfo;
              alert(`✅ Storage bucket test successful!\n\nBucket info:\n- Name: ${bucketInfo?.name}\n- Files: ${bucketInfo?.fileCount || 0}\n- Type: ${bucketInfo?.isPrivate ? 'Private (Secure)' : 'Public'}\n- Can Write: ${bucketInfo?.canWrite ? 'Yes' : 'No'}\n- Can Read: ${bucketInfo?.canRead ? 'Yes' : 'No'}\n\nAvailable buckets: ${bucketInfo?.availableBuckets?.map((b: any) => `${b.name} (${b.public ? 'public' : 'private'})`).join(', ')}`);
            } else {
              alert(`❌ Storage bucket test failed: ${result.error}`);
            }
          } catch (error) {
            console.error('Storage test error:', error);
            alert('❌ Storage bucket test failed');
          } finally {
            set({ isLoading: false });
          }
        },

        // NEW: Toggle online/offline mode
        toggleOnlineMode: () => {
          set((state) => ({ isOnline: !state.isOnline }));
        }
      }),
      {
        name: 'blinds-enquiry-store',
        // Only persist some fields, not the database enquiries
        partialize: (state) => ({
          currentEnquiry: state.currentEnquiry,
          currentStep: state.currentStep,
          isOnline: state.isOnline
        })
      }
    )
  )
);